// frontend/src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode'; // Importação nomeada
import config from '../config';
import api from '../services/api';

const AuthContext = createContext();

// Tempo de inatividade (em milissegundos). 4 horas
const INACTIVITY_LIMIT = 4 * 60 * 60 * 1000;

// Antecedência com que o token é renovado antes de expirar (em segundos).
const RENEW_WINDOW_SECONDS = 5 * 60;

export const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState(null);
    const [loading, setLoading] = useState(true); // Estado de carregamento

    // Empresa (tenant) em que o usuário está operando. O token só carrega o
    // tenantId; o nome/logo vêm de GET /empresas/atual e alimentam o cabeçalho.
    const [empresa, setEmpresa] = useState(null);
    const [empresaCarregando, setEmpresaCarregando] = useState(false);
    const [empresaErro, setEmpresaErro] = useState(false);
    const inactivityTimer = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const listenersAddedRef = useRef(false);
    const tokenCheckInterval = useRef(null); // Interval para verificação contínua
    const renovacaoRef = useRef(null); // Renovação em andamento (promessa compartilhada)

    const refreshToken = () => {
        // Uma renovação por vez. Quem chamar durante uma renovação em andamento
        // recebe a MESMA promessa e espera o resultado dela — devolver null aqui
        // faria o chamador seguir como se a renovação tivesse falhado.
        if (renovacaoRef.current) return renovacaoRef.current;

        renovacaoRef.current = (async () => {
            try {
                // Verifica inatividade
                if (Date.now() - lastActivityRef.current > INACTIVITY_LIMIT) {
                    logout();
                    return null;
                }

                const storedRefreshToken = localStorage.getItem('refreshToken');
                if (!storedRefreshToken) {
                    console.log('Refresh token não encontrado');
                    logout();
                    return null;
                }

                console.log('Tentando renovar token...');
                const response = await fetch(`${config.API_BASE_URL}/auth/refresh-token`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refreshToken: storedRefreshToken }),
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const data = await response.json();

                if (!data.token) {
                    throw new Error('Novo token não recebido na resposta');
                }

                console.log('Token renovado com sucesso');
                login(data.token); // Atualiza o access token
                return data.token;
            } catch (error) {
                console.error('❌ Erro ao renovar o token:', error);
                console.log('🔄 Redirecionando para login devido a falha na renovação...');
                logout();
                return null;
            } finally {
                renovacaoRef.current = null;
            }
        })();

        return renovacaoRef.current;
    };

    // Função para iniciar o timer de inatividade
    const startInactivityTimer = () => {
        if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        inactivityTimer.current = setTimeout(() => {
            logout();
        }, INACTIVITY_LIMIT);
    };

    // Função chamada apenas por eventos reais de usuário
    const handleUserActivity = () => {
        // Atualiza somente se houver atividade real do usuário
        lastActivityRef.current = Date.now();
        startInactivityTimer();
    };

    // Função para verificar constantemente se o token expirou
    const checkTokenExpiration = () => {
        // NÃO depender do state `auth` aqui: o intervalo é criado na montagem e
        // congelava um `auth` ainda nulo, então este guard abortava a verificação
        // para sempre e o token nunca era renovado em segundo plano — o usuário só
        // descobria a sessão vencida quando ia salvar. O token no localStorage é a
        // fonte de verdade, e `logout()` limpa o intervalo junto com ele.
        const token = localStorage.getItem('token');
        if (!token) return;

        try {
            const decoded = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            const timeUntilExpiry = decoded.exp - currentTime;

            // Renova com folga, e tambem quando ja expirou: derrubar a sessao aqui
            // seria pior que tentar renovar, porque o refresh token vale 7 dias e
            // costuma resolver. `refreshToken()` faz logout sozinho se nao conseguir.
            //
            // A janela de 30s anterior era arriscada: em aba em segundo plano o
            // navegador limita os timers e ela passava sem ser vista.
            if (timeUntilExpiry < RENEW_WINDOW_SECONDS) {
                console.log('⚠️ Token expirado ou expirando - tentando renovar automaticamente...');
                refreshToken();
            }
        } catch (error) {
            console.error('Erro na verificação automática do token:', error);
            logout();
        }
    };

    // Iniciar verificação contínua do token
    const startTokenCheck = () => {
        if (tokenCheckInterval.current) return; // Já está rodando
        
        console.log('🔄 Iniciando verificação automática de token (a cada 5 segundos)...');
        tokenCheckInterval.current = setInterval(() => {
            checkTokenExpiration();
        }, 5000); // Verifica a cada 5 segundos
    };

    // Parar verificação contínua do token
    const stopTokenCheck = () => {
        if (tokenCheckInterval.current) {
            clearInterval(tokenCheckInterval.current);
            tokenCheckInterval.current = null;
            console.log('⏹️ Parando verificação automática de token...');
        }
    };

    useEffect(() => {
        // `loading` só pode ser liberado DEPOIS que a renovação terminar: como
        // `PrivateRoute` manda para /login assim que `loading` vira false com
        // `auth` ainda nulo, liberar antes jogava o usuário para o login mesmo
        // quando o refresh token resolveria a sessão.
        const inicializar = async () => {
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    const decoded = jwtDecode(token);
                    const currentTime = Date.now() / 1000;
                    if (decoded.exp < currentTime) {
                        // Se o token expirou, tenta renová-lo
                        await refreshToken();
                    } else {
                        setAuth({
                            token,
                            user: {
                                id: decoded.user.id,
                                role: decoded.user.role,
                                permissions: decoded.user.permissions,
                                tenantId: decoded.user.tenantId,
                                name: decoded.user.name,
                                photoUrl: decoded.user.photoUrl,
                            },
                        });
                        // Iniciar verificação automática quando há um token válido
                        startTokenCheck();
                    }
                } catch (error) {
                    console.error('Erro ao decodificar token:', error);
                    logout();
                }
            }
            setLoading(false);
        };

        inicializar();
    }, []);

    // Busca a empresa atual sempre que o usuário (ou o tenant dele) muda.
    // A dependência é o tenantId, e não o objeto `auth`: a renovação automática
    // do token troca `auth` de identidade a cada poucos minutos e refazer a
    // requisição a cada renovação seria desperdício.
    const carregarEmpresaAtual = async () => {
        setEmpresaCarregando(true);
        setEmpresaErro(false);
        try {
            const resposta = await api.get('/empresas/atual');
            setEmpresa(resposta.data);
            return resposta.data;
        } catch (error) {
            console.error('Erro ao carregar a empresa atual:', error);
            setEmpresa(null);
            setEmpresaErro(true);
            return null;
        } finally {
            setEmpresaCarregando(false);
        }
    };

    useEffect(() => {
        const tenantId = auth?.user?.tenantId;
        if (!tenantId) {
            setEmpresa(null);
            setEmpresaCarregando(false);
            setEmpresaErro(false);
            return;
        }
        carregarEmpresaAtual();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth?.user?.tenantId]);

    // Adiciona os event listeners de atividade apenas uma vez após o login inicial
    useEffect(() => {
        if (auth && !listenersAddedRef.current) {
            window.addEventListener('mousemove', handleUserActivity);
            window.addEventListener('keydown', handleUserActivity);
            window.addEventListener('keypress', handleUserActivity);
            window.addEventListener('input', handleUserActivity);
            window.addEventListener('scroll', handleUserActivity);
            window.addEventListener('click', handleUserActivity);
            window.addEventListener('touchstart', handleUserActivity);
            listenersAddedRef.current = true;
            // Inicia o timer de inatividade assim que o usuário loga
            startInactivityTimer();
        }
        // Remoção dos listeners quando o usuário sair (logout)
        return () => {
            if (listenersAddedRef.current) {
                window.removeEventListener('mousemove', handleUserActivity);
                window.removeEventListener('keydown', handleUserActivity);
                window.removeEventListener('keypress', handleUserActivity);
                window.removeEventListener('input', handleUserActivity);
                window.removeEventListener('scroll', handleUserActivity);
                window.removeEventListener('click', handleUserActivity);
                window.removeEventListener('touchstart', handleUserActivity);
                listenersAddedRef.current = false;
            }
            if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        };
    }, [auth]);

    const login = (token, newRefreshToken) => {
        localStorage.setItem('token', token);
        // Atualiza o refresh token somente se um novo for informado
        if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
        }
        try {
            const decoded = jwtDecode(token);
            setAuth({
                token,
                user: {
                    id: decoded.user.id,
                    role: decoded.user.role,
                    permissions: decoded.user.permissions,
                    tenantId: decoded.user.tenantId,
                    name: decoded.user.name,
                    photoUrl: decoded.user.photoUrl
                },
            });
            // Iniciar verificação automática após login
            startTokenCheck();
        } catch (error) {
            console.error('AuthContext - Erro ao decodificar o token durante login:', error);
        }
    };

    const logout = () => {
        console.log('🚪 Executando logout...');
        
        // Parar verificação automática
        stopTokenCheck();
        
        // Limpar todos os timers
        if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        
        // Remover tokens
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        
        // Limpar estado
        setAuth(null);
        setEmpresa(null);
        setEmpresaCarregando(false);
        setEmpresaErro(false);
        
        // Redirecionar imediatamente
        console.log('🔄 Redirecionando para tela de login...');
        setTimeout(() => {
            window.location.href = '/login';
        }, 100);
    };

    return (
        <AuthContext.Provider
            value={{
                auth,
                login,
                logout,
                loading,
                empresa,
                empresaCarregando,
                empresaErro,
                recarregarEmpresa: carregarEmpresaAtual,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);